// Client-side S3 access using short-lived credentials vended by the server's
// Cognito controller (POST /token). The S3Client is built lazily and cached
// for the lifetime of the page; credentials auto-expire after ~15 min, so call
// refresh() if you get an expired-credentials error on a long-lived page.
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity'
import { fromCognitoIdentity } from '@aws-sdk/credential-provider-cognito-identity'
import {
  S3Client,
  ListObjectsV2Command,
  GetObjectCommand,
  PutObjectCommand,
} from '@aws-sdk/client-s3'
import { Upload } from '@aws-sdk/lib-storage'
import { getSignedUrl } from '@aws-sdk/s3-request-presigner'

let clientPromise = null
let cognito = null

// Fetch a token from the server and build an S3Client scoped to this user.
function buildClient() {
  return fetch('/token', { method: 'POST' })
    .then((res) => {
      if (!res.ok) throw new Error(`/token failed: ${res.status}`)
      return res.json()
    })
    .then(({ tok, region }) => {
      const defaults = { region: region || 'us-east-1' }
      if (!cognito) cognito = new CognitoIdentityClient(defaults)
      const credentials = fromCognitoIdentity({ ...tok, client: cognito })
      return new S3Client({ credentials, ...defaults })
    })
}

// Cached client promise. Call refresh() to force new credentials.
export function s3() {
  if (!clientPromise) clientPromise = buildClient()
  return clientPromise
}

export function refresh() {
  clientPromise = buildClient()
  return clientPromise
}

// List objects (and common prefixes / "folders") under a bucket + prefix.
export async function listObjects(Bucket, Prefix = '') {
  const client = await s3()
  const out = await client.send(
    new ListObjectsV2Command({ Bucket, Prefix, Delimiter: '/' })
  )
  return {
    prefixes: (out.CommonPrefixes ?? []).map((p) => p.Prefix),
    objects: out.Contents ?? [],
  }
}

// Upload a File/Blob/string. Uses lib-storage's Upload so large files are
// automatically split into multipart uploads.
export async function uploadFile(Bucket, Key, Body, ContentType) {
  const client = await s3()
  const upload = new Upload({
    client,
    params: { Bucket, Key, Body, ContentType: ContentType || Body?.type },
  })
  return upload.done()
}

// Small helper for direct PutObject (no multipart) when you know it's small.
export async function putObject(Bucket, Key, Body, ContentType) {
  const client = await s3()
  return client.send(new PutObjectCommand({ Bucket, Key, Body, ContentType }))
}

// Presigned GET URL, valid for `expiresIn` seconds (default 1 hour).
export async function signedUrl(Bucket, Key, expiresIn = 3600) {
  const client = await s3()
  return getSignedUrl(client, new GetObjectCommand({ Bucket, Key }), { expiresIn })
}
